import {
  KeyboardArrowDownOutlined,
  KeyboardArrowUpOutlined
} from '@mui/icons-material';
import { Grid } from '@mui/material';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import COLORS from '../../colors';
import { Button } from '../Button';
import { Divider } from '../Divider';
import { Input } from '../Input';
import { ContentContainer } from '../containers';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { RootState } from '../../redux/store';
import { DURATION_SECONDS, RIGHT_SECTION_LABEL } from '../../constants';
import mapBurstToNumber from '../BudgetSplitSection/mapBurstToNumber';
import { updateField } from '../../redux/features/campaignDetail/campaignDetailSlice';
import { REDUX_KEY } from '../../redux/constants';
import { isOnlyNumbers, removeCommas } from '../../util';

interface CampaignTargetProps {
  label: RIGHT_SECTION_LABEL;
  icon: React.ReactNode;
  usedValue: number;
  totalValue: number;
  durations: Record<DURATION_SECONDS, boolean>;
}

interface TrackProps {
  width: string;
  isOverBudgeted?: boolean;
}

interface ItemTotalProps {
  isOverBudgeted?: boolean;
}

interface ExtraProps {
  showExtraSection: boolean;
}

const StyledContainer = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
  justify-content: space-between;
`;

const StyledTitleContainer = styled.div<ExtraProps>`
  display: ${(props) => (props.showExtraSection ? 'block' : 'flex')};
  width: 100%;
  justify-content: space-between;
  align-items: center;
`;

const StyledTitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const StyledTitle = styled.p`
  font-size: 13.33px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
  color: ${COLORS.NEUTRAL_500};
`;

const StyledValueWrapper = styled.div<ExtraProps>`
  display: flex;
  width: ${(props) => (props.showExtraSection ? '100%' : 'auto')};
  justify-content: space-between;
`;

const StyledValue = styled.p`
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  color: ${COLORS.NEUTRAL_500};
`;

const StyledValueHighlight = styled.span`
  color: ${COLORS.NEUTRAL_200};
`;

const StyledTrackContainer = styled.div`
  display: flex;
  width: 100%;
  padding-top: 12px;
`;

const StyledTrack = styled.div<TrackProps>`
  background-color: ${COLORS.PRIMARY_CONTAINER};
  height: 6px;
  border-radius: 6px;
  width: ${(props) => props.width};
`;

const StyledTrackHighlight = styled.div<TrackProps>`
  background-color: ${(props) =>
    props.isOverBudgeted ? COLORS.ERROR : COLORS.PRIMARY_500};
  height: 6px;
  border-radius: 6px;
  width: ${(props) => props.width};
`;

const StyledIconContainer = styled.div`
  display: flex;
  width: 38px;
  height: 32px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border-radius: 32px;
  background-color: ${COLORS.SECONDARY_CONTAINER};
`;

const StyledButtonContainer = styled.div`
  display: flex;
  width: 38px;
  height: 32px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border-radius: 32px;
  background-color: ${COLORS.NEUTRAL_900};
  cursor: pointer;

  &:hover {
    background-color: ${COLORS.BUTTON_SECONDARY_HOVER};
  }
`;

const StyledExtraSection = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-top: 16px;
`;

const StyledPercentageContainer = styled.div<ItemTotalProps>`
  background-color: ${(props) =>
    props.isOverBudgeted ? COLORS.ERROR : COLORS.SECONDARY_CONTAINER};
  border-radius: 4px;
  box-sizing: border-box;
  padding: 4px;
  margin: 0;
`;

const StyledPercentage = styled.p<ItemTotalProps>`
  font-size: 12px;
  font-weight: 700;
  color: ${(props) =>
    props.isOverBudgeted ? COLORS.BLACK : COLORS.NEUTRAL_200};
`;

const StyledItemRow = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  margin: 4px 0;
`;

const StyledItemTitle = styled.p`
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  color: ${COLORS.NEUTRAL_500};
`;

const StyledItemValue = styled.p`
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  color: ${COLORS.NEUTRAL_200};
`;

const StyledItemSymbol = styled.p`
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  color: ${COLORS.NEUTRAL_500};
  display: inline;
  padding-right: 4px;
`;

const StyledItemTotal = styled.p<ItemTotalProps>`
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  color: ${(props) =>
    props.isOverBudgeted ? COLORS.ERROR : COLORS.PRIMARY_500};
`;

const StyledDividerWrapper = styled.div`
  margin: 4px 0;
`;

const CampaignTargets = ({
  label,
  icon,
  totalValue,
  usedValue,
  durations
}: CampaignTargetProps) => {
  const dispatch = useAppDispatch();
  const { burst } = useAppSelector((state: RootState) => state);
  const [showExtraSection, setShowExtraSection] = useState<boolean>(false);
  const [showForm, setShowForm] = useState<boolean>(false);
  const [value, setValue] = useState<number>(totalValue);
  const [percentage, setPercentage] = useState(0);
  const [userInput, setUserInput] = useState('');

  const toggleExtraSection = () => {
    setShowExtraSection(!showExtraSection);
  };

  const displayBurstTotal = (duration: DURATION_SECONDS): number => {
    if (label === RIGHT_SECTION_LABEL.BUDGET) {
      return burst[duration].calculatedTotalBudget;
    }

    if (label === RIGHT_SECTION_LABEL.TARPS) {
      return burst[duration].calculatedTotalTarps;
    }

    if (label === RIGHT_SECTION_LABEL.IMPACT) {
      return burst[duration].calculatedTotalImpact;
    }

    return -1;
  };

  const format = (amount: number): string => {
    const rounded = Math.round(amount);
    const formattedResult = rounded
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    if (label === RIGHT_SECTION_LABEL.BUDGET) {
      return '$' + formattedResult;
    }

    return formattedResult;
  };

  const calculateTotalPercentageUsed = (): number => {
    const used = (usedValue / totalValue) * 100;
    return Math.round(used);
  };

  const handleTargetUpdate = () => {
    setShowForm(false);
    setUserInput('');
    dispatch(
      updateField({
        fieldName: mapLabelToReduxKey(),
        value: Number(removeCommas(userInput))
      })
    );
  };

  const mapLabelToReduxKey = () => {
    switch (label) {
      case RIGHT_SECTION_LABEL.BUDGET:
        return REDUX_KEY.GROSS_BUDGET;
      case RIGHT_SECTION_LABEL.TARPS:
        return REDUX_KEY.TARGET_TARP;
      case RIGHT_SECTION_LABEL.IMPACT:
        return REDUX_KEY.TARGET_IMPACT;
    }
  };

  const handleInputUpdate = (value: string): void => {
    if (!isOnlyNumbers(value)) {
      return;
    }

    setUserInput(Number(removeCommas(value)).toLocaleString());
  };

  const handleCancel = () => {
    setShowForm(false);
    setUserInput('');
  };

  useEffect(() => {
    setPercentage(calculateTotalPercentageUsed());

    if (totalValue > 0) {
      setValue(totalValue);
      setShowForm(false);
    } else {
      setValue(0);
    }
  }, [totalValue, usedValue]);

  return (
    <ContentContainer>
      <StyledContainer>
        <StyledIconContainer>{icon}</StyledIconContainer>
        {value !== undefined && value !== 0 ? (
          <>
            <StyledTitleWrapper>
              <StyledTitleContainer showExtraSection={showExtraSection}>
                <StyledTitle>{label}</StyledTitle>
                <StyledValueWrapper showExtraSection={showExtraSection}>
                  <StyledValue>
                    <StyledValueHighlight>
                      {format(usedValue)}
                    </StyledValueHighlight>
                    / {totalValue > 0 ? format(totalValue) : 'Not Entered'}
                  </StyledValue>
                  {showExtraSection && (
                    <StyledPercentageContainer
                      isOverBudgeted={percentage > 100}
                    >
                      <StyledPercentage isOverBudgeted={percentage > 100}>
                        {totalValue > 0
                          ? percentage + '%'
                          : label + ' Target Not Entered'}
                      </StyledPercentage>
                    </StyledPercentageContainer>
                  )}
                </StyledValueWrapper>
              </StyledTitleContainer>
              <StyledTrackContainer>
                <StyledTrackHighlight
                  width={percentage + '%'}
                  isOverBudgeted={percentage > 100}
                />
                <StyledTrack width={100 - percentage + '%'} />
              </StyledTrackContainer>
              {showExtraSection && (
                <StyledExtraSection>
                  {Object.entries(durations).map(
                    ([duration, isBurstSelected]) => {
                      const durationSeconds = duration as DURATION_SECONDS;
                      const burstNumber = mapBurstToNumber(durationSeconds);
                      return (
                        isBurstSelected && (
                          <StyledItemRow key={duration}>
                            <StyledItemTitle>
                              {burstNumber}" Duration Cost
                            </StyledItemTitle>
                            <StyledItemValue>
                              {format(displayBurstTotal(durationSeconds))}
                            </StyledItemValue>
                          </StyledItemRow>
                        )
                      );
                    }
                  )}
                  <StyledDividerWrapper>
                    <Divider />
                  </StyledDividerWrapper>
                  <StyledItemRow>
                    <StyledItemTitle>Total Used</StyledItemTitle>
                    <StyledItemValue>
                      <StyledItemSymbol>=</StyledItemSymbol>
                      {format(usedValue)}
                    </StyledItemValue>
                  </StyledItemRow>
                  <StyledItemRow>
                    <StyledItemTitle>Target {label}</StyledItemTitle>
                    <StyledItemTitle>{format(totalValue)}</StyledItemTitle>
                  </StyledItemRow>
                  <StyledDividerWrapper>
                    <Divider />
                  </StyledDividerWrapper>
                  <StyledItemRow>
                    <StyledItemTotal isOverBudgeted={percentage > 100}>
                      Remaining {label}
                    </StyledItemTotal>
                    <StyledItemTotal isOverBudgeted={percentage > 100}>
                      {format(totalValue - usedValue)}
                    </StyledItemTotal>
                  </StyledItemRow>
                </StyledExtraSection>
              )}
            </StyledTitleWrapper>
            <StyledButtonContainer onClick={toggleExtraSection}>
              {showExtraSection ? (
                <KeyboardArrowUpOutlined />
              ) : (
                <KeyboardArrowDownOutlined />
              )}
            </StyledButtonContainer>
          </>
        ) : (
          <StyledTitleWrapper>
            <StyledTitleContainer showExtraSection={true}>
              <StyledTitle>{label}</StyledTitle>
              {!showForm && (
                <StyledValue>
                  <StyledValueHighlight>Not Entered</StyledValueHighlight>
                </StyledValue>
              )}
            </StyledTitleContainer>
            {showForm ? (
              <>
                <Grid sx={{ marginTop: '24px' }} item xs={6}>
                  <Input
                    label={`Campaign ${label}`}
                    helperText={
                      label === RIGHT_SECTION_LABEL.BUDGET
                        ? 'Currency: NZD'
                        : ''
                    }
                    value={
                      userInput === '0' || userInput === '' ? '' : userInput
                    }
                    onChange={(value: string) => handleInputUpdate(value)}
                  />
                </Grid>
                <Grid
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '8px'
                  }}
                  item
                  xs={6}
                >
                  <Button
                    label="Cancel"
                    backgroundColor="transparent"
                    hoverBackgroundColor={COLORS.BUTTON_TERTIARY_HOVER}
                    textColor={COLORS.PRIMARY_500}
                    showShadow={false}
                    onClick={handleCancel}
                  />
                  <Button
                    label="Update"
                    backgroundColor={COLORS.PRIMARY_500}
                    hoverBackgroundColor={COLORS.BUTTON_PRIMARY_HOVER}
                    textColor={COLORS.PRIMARY_800}
                    onClick={handleTargetUpdate}
                  />
                </Grid>
              </>
            ) : (
              <Grid
                sx={{ display: 'flex', justifyContent: 'flex-end' }}
                item
                xs={6}
              >
                <Button
                  label={`Add ${label}`}
                  backgroundColor={COLORS.NEUTRAL_900}
                  hoverBackgroundColor={COLORS.BUTTON_SECONDARY_HOVER}
                  textColor={COLORS.PRIMARY_500}
                  showShadow={false}
                  onClick={() => setShowForm(true)}
                />
              </Grid>
            )}
          </StyledTitleWrapper>
        )}
      </StyledContainer>
    </ContentContainer>
  );
};

export default CampaignTargets;
